import { getCurrentInstance, onBeforeUnmount, ref } from 'vue';
import { resolveMethodName } from './mapping';

export function createService({
  connection,
  failFn = () => {},
  autoOffInsideComponentScope = true,
}) {
  const connected = ref(false);
  const reconnection = ref(false);
  const invokeQueue = [];
  const activeListenersSet = new Set();

  connection.onclose(async (e) => {
    if (e) {
      connected.value = false;
      reconnection.value = true;
      await startConnection();
    }

    failFn(e);
  });

  async function startConnection() {
    try {
      await connection.start();
      connected.value = true;
      reconnection.value = false;

      while (invokeQueue.length) {
        const action = invokeQueue.shift();
        // "action?.()" syntax isn't transpiled by TS due to esnext target
        // and would break projects using the package
        action && action();
      }
      console.log('Connected');
    } catch (error) {
      connected.value = false;

      setTimeout(async () => {
        console.log('Reconnecting 2...');
        reconnection.value = true;
        await startConnection();
      }, 1000);

      failFn(error);
    }
  }

  async function init() {
    if (connected.value || reconnection.value) {
      return;
    }
    await startConnection();
  }

  async function close() {
    await connection.stop();
    connected.value = false;
  }

  function invoke(methodName, ...payload) {
    return new Promise((resolve, reject) => {
      const invokeFn = () =>
        connection
          .invoke(resolveMethodName(methodName), ...payload)
          .then(resolve)
          .catch(reject);

      if (connected.value) {
        void invokeFn();
      } else {
        invokeQueue.push(invokeFn);
      }
    });
  }

  function on(methodName, callback) {
    const originalMethodName = resolveMethodName(methodName);

    connection.on(originalMethodName, callback);

    if (autoOffInsideComponentScope) {
      // Auto-unregister listener if inside a component
      const instance = getCurrentInstance();
      if (instance) {
        activeListenersSet.add(callback);

        onBeforeUnmount(() => {
          if (activeListenersSet.delete(callback)) {
            off(methodName, callback);
          }
        });
      }
    }
  }

  // function once<Key extends SignalREventKey>(
  //   methodName: Key,
  //   callback: (...payload: SignalREventPayload<Key>) => void,
  //   options: SignalROnOptions<SignalREventPayload<Key>> = {},
  // ) {
  //   on<Key>(methodName, callback, { ...options, once: true });
  // }

  function off(methodName, callback) {
    const originalMethodName = resolveMethodName(methodName);

    if (callback) {
      connection.off(originalMethodName, callback);
    } else {
      connection.off(originalMethodName);
    }
  }

  return {
    init,
    close,
    connected,
    invoke,
    on,
    off,
  };
}
